import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RephrasingService } from '../../features/rephrasing/RephrasingService';
import { ChatService } from '../../features/chat/ChatService';
import { AIOrchestrator } from '../../ai/orchestration/AIOrchestrator';
import { createAIProvider } from '../../ai/providers/factory';
import { EditorView } from './EditorView';
import { AskView, forgetLastOpenAskConversation } from './AskView';
import { ReviewView } from './ReviewView';
import { ProofreadView } from './ProofreadView';
import { ProfileView } from './ProfileView';
import { AgentView } from './AgentView';
import { ProjectSelector } from './ProjectSelector';
import { CrossCheckResultsView, type MccBesidePdfLayoutChangeDetail } from './CrossCheckResultsView';
import { forgetUnsentAiSuggestions } from './CrossCheckClauseCard';
import { MasterCrossChecksInitiation } from './MasterCrossChecksInitiation';
import { Login } from './Login';
import { signOut as signOutFirebase } from '../../services/firebaseAuth';
import { SolutionsGrid, type SolutionId } from './SolutionsGrid';
import { BottomNav, NavTab } from './BottomNav';
import { ReviewRequiredModal } from './ReviewRequiredModal';
import apiService from '../../services/api';
import { requestCache } from '../../services/RequestCache';
import { claimUserState, stateOwner, userOfToken } from '../../utils/paneUser';
import { log, logError } from '../../utils/logging';
import { hasReviewPartyContextComplete } from '../../utils/reviewPartyContext';
import {
  documentIdentitiesMatch,
  normalizeDocumentName,
  type DocumentIdentity,
} from '../../utils/reviewDocumentIdentity';
import { resolveDocumentIdentity } from '../../utils/resolveDocumentIdentity';
import { getFontScale, getUiScaleStyle } from '../../utils/addInPreferences';
import {
  clearDocumentScopedSelection,
  loadSessionNavigation,
  saveSessionNavigation,
  snapshotHasRestorableSelection,
  type SessionNavigationSnapshot,
  type SolutionsSubView,
} from '../../utils/sessionNavigationStorage';
import {
  matchesForSolution,
  resolveDocumentSolution,
  type FilenameMatch,
} from '../../utils/documentSolutionMatch';
import { FilenameMatchPicker } from './FilenameMatchPicker';
import { DocumentExporter } from '../../word/utils/DocumentExporter';
import { startWordParagraphEditCapture } from '../../word/api/WordParagraphEditCapture';
import { isSubProjectAwaitingCrossCheckStart } from '../../utils/crossCheckStartGate';
import {
  markSessionStart,
  installUserActivityListeners,
  shouldForceLogout,
  getSessionStart,
} from '../../utils/sessionActivity';
import {
  notifyUserActivityPing,
  resetActivityPing,
  sendPaneWarmup,
} from '../../services/activityPingService';
import { directionForLanguage } from '../../i18n/config';
import { isCrossCheckWorkflowError } from '../../utils/crossCheckWorkflowStatus';
import './TaskPane.css';

export type { SolutionsSubView };

/** Master Cross Checks and Construction Loan share filename-based project inference (same API + UI). */
function isCrossCheckFilenameInferenceSolution(id: SolutionsSubView): boolean {
  return id === 'master_cross_checks' || id === 'construction_loan';
}

interface TaskPaneProps {
  /** Builds the pane again from nothing: someone else signed in to it, or the user signed out. */
  onRestart?: () => void;
}

const TaskPane: React.FC<TaskPaneProps> = ({ onRestart }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  /**
   * The user this pane is open for. Another user's token, signed in here or in another Word window (the
   * token is shared between them), starts the pane over, so nothing of this user's shows under theirs.
   */
  const paneUserRef = React.useRef<string | null>(null);
  const initialSession = React.useMemo(() => {
    const user = userOfToken(apiService.getCurrentAuthToken());
    paneUserRef.current = user;
    // Opened signed in: the place kept on this computer is restored only when it is this user's.
    if (user) claimUserState(user, { adoptUnowned: true });
    return loadSessionNavigation();
  }, []);
  /** Keeps the pane's place for next time, unless the state on this computer is now someone else's. */
  const keepSession = React.useCallback((snapshot: SessionNavigationSnapshot) => {
    const user = paneUserRef.current;
    if (user && stateOwner() === user) saveSessionNavigation(snapshot);
  }, []);
  /** Starts the pane over: nothing of what was open, or remembered in memory, is kept. */
  const restartPane = React.useCallback(() => {
    requestCache.clear();
    forgetLastOpenAskConversation();
    forgetUnsentAiSuggestions();
    onRestart?.();
  }, [onRestart]);
  /** Mutable view of the restored snapshot; cleared once it proves to be another document's. */
  const restoredSessionRef = React.useRef<SessionNavigationSnapshot>(initialSession);
  const [viewMode, setViewMode] = React.useState<NavTab>(initialSession.viewMode);
  const [fontScale, setFontScale] = React.useState(getFontScale);
  const handlePreferencesChange = React.useCallback(() => {
    setFontScale(getFontScale());
  }, []);
  /** Keep Review mounted after side-ID completes so document review can finish in the background while the user is on Editor. */
  const [reviewKeepAlive, setReviewKeepAlive] = React.useState(initialSession.reviewKeepAlive);
  const [reviewBusy, setReviewBusy] = React.useState(false);
  const handleReviewBusyChange = React.useCallback((busy: boolean) => {
    setReviewBusy(busy);
  }, []);
  const [documentIdentity, setDocumentIdentity] = React.useState<DocumentIdentity | null>(null);
  /**
   * Solutions stays mounted, hidden, once it has been shown: going to Editor and back finds it as it was
   * left, without loading the client project again. Another document in this pane starts it over.
   */
  const [solutionsKeepAlive, setSolutionsKeepAlive] = React.useState(initialSession.viewMode === 'clauses');
  /** Bumped when the pane's document changes (Save As): Solutions is built again from nothing. */
  const [solutionsGeneration, setSolutionsGeneration] = React.useState(0);
  /** Where Solutions was scrolled to while on screen; a hidden view loses its scroll, so it is put back. */
  const solutionsScrollRef = React.useRef({ view: 0, stack: 0 });
  const [solutionsSubView, setSolutionsSubView] = React.useState<SolutionsSubView>(initialSession.solutionsSubView);
  const [selectedSubProjectId, setSelectedSubProjectId] = React.useState<string>(initialSession.selectedSubProjectId);
  const [selectedProjectId, setSelectedProjectId] = React.useState<string>(initialSession.selectedProjectId);
  const [refreshClausesKey, setRefreshClausesKey] = React.useState(0);
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(() => apiService.isAuthenticated());
  const [filenameMatchOptions, setFilenameMatchOptions] = React.useState<FilenameMatch[] | null>(null);
  const [showManualProjectSelect, setShowManualProjectSelect] = React.useState<boolean>(false);
  /** True while resolving the open document filename to a project (MCC / Construction Loan). */
  const [filenameMatchLoading, setFilenameMatchLoading] = React.useState(false);
  const [autoDetectTrigger, setAutoDetectTrigger] = React.useState(0);
  /**
   * Solution-agnostic lookup of the open document, run once per document before the
   * solutions grid is shown: a document that already belongs to a client project opens
   * on that project's solution, whatever solution was open last.
   */
  const [documentScanLoading, setDocumentScanLoading] = React.useState(false);
  /** Candidates spanning more than one solution — picked at the solutions level. */
  const [documentScanOptions, setDocumentScanOptions] = React.useState<FilenameMatch[] | null>(null);
  const documentScannedForRef = React.useRef<string | null>(null);
  /**
   * False while a restored project selection has not yet been confirmed to belong to
   * the open document. Solutions stays on a spinner until then, so a stale selection
   * from another document is never rendered (nor its backend calls fired).
   */
  const [documentScopeSettled, setDocumentScopeSettled] = React.useState<boolean>(
    () =>
      initialSession.solutionsSubView === 'list' &&
      !initialSession.selectedProjectId &&
      !initialSession.selectedSubProjectId,
  );
  /** Result of the solution-agnostic lookup, reused by the per-solution auto-detect. */
  const documentMatchCacheRef = React.useRef<{ documentName: string; matches: FilenameMatch[] } | null>(null);
  const [showMccCreateFlow, setShowMccCreateFlow] = React.useState(false);
  const [showMccStartCrossCheckResume, setShowMccStartCrossCheckResume] = React.useState(false);
  const [resumeParentProjectId, setResumeParentProjectId] = React.useState<string | null>(null);
  const dismissedStartCrossCheckSubProjectIdRef = React.useRef<string | null>(null);
  /** Sub-project where the user clicked Start Cross-Check; suppress resume gate until the backend acknowledges. */
  const crossCheckStartInitiatedSubProjectIdRef = React.useRef<string | null>(null);
  const [subProjectRefreshTrigger, setSubProjectRefreshTrigger] = React.useState(0);
  const [crossCheckStartError, setCrossCheckStartError] = React.useState<string | null>(null);
  /** MCC / Construction Loan: Beside SA PDF docks to the left of the task pane (full height), independent of Project Settings. */
  const [mccBesidePdfDocked, setMccBesidePdfDocked] = React.useState(false);
  const [mccBesidePdfPct, setMccBesidePdfPct] = React.useState(47);
  /** Bumped when entering MCC/CL from the solutions list so CrossCheck can scroll the workflow steps into view. */
  const [mccWorkflowScrollRequestToken, setMccWorkflowScrollRequestToken] = React.useState(0);
  const clausesViewRef = React.useRef<HTMLDivElement>(null);
  const mccPdfBesideDockRef = React.useRef<HTMLDivElement>(null);
  const mccBesideHandleSlotRef = React.useRef<HTMLDivElement>(null);
  const mccBesideRightStackRef = React.useRef<HTMLDivElement>(null);
  const pendingMccRightStackScrollRef = React.useRef<number | null>(null);
  const pendingMccClausesViewScrollRef = React.useRef<number | null>(null);

  const handleMccBesidePdfLayoutChange = React.useCallback((detail: MccBesidePdfLayoutChangeDetail) => {
    setMccBesidePdfPct(detail.pdfWidthPct);
    setMccBesidePdfDocked((prevDocked) => {
      if (detail.docked && !prevDocked) {
        // Scroll roots change when the beside column opens; CrossCheckResultsView
        // repositions the active clause card header instead of copying scrollTop.
        pendingMccRightStackScrollRef.current = null;
      } else if (!detail.docked && prevDocked) {
        // Same on close — CrossCheckResultsView aligns the clause header after undock.
        pendingMccClausesViewScrollRef.current = null;
      }
      return detail.docked;
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!mccBesidePdfDocked) return;
    const y = pendingMccRightStackScrollRef.current;
    if (y == null || !mccBesideRightStackRef.current) return;
    mccBesideRightStackRef.current.scrollTop = y;
    pendingMccRightStackScrollRef.current = null;
  }, [mccBesidePdfDocked]);

  React.useLayoutEffect(() => {
    if (mccBesidePdfDocked) return;
    const y = pendingMccClausesViewScrollRef.current;
    if (y == null || !clausesViewRef.current) return;
    clausesViewRef.current.scrollTop = y;
    pendingMccClausesViewScrollRef.current = null;
  }, [mccBesidePdfDocked]);

  /** Solutions grid and MCC/CL share `.clauses-view`; scroll position persists across sub-views. */
  const prevSolutionsSubViewRef = React.useRef<SolutionsSubView>(solutionsSubView);
  React.useLayoutEffect(() => {
    const prev = prevSolutionsSubViewRef.current;
    prevSolutionsSubViewRef.current = solutionsSubView;

    if (viewMode !== 'clauses') return;

    const enteredCrossCheckFromList =
      prev === 'list' &&
      isCrossCheckFilenameInferenceSolution(solutionsSubView) &&
      solutionsSubView !== 'list';

    if (!enteredCrossCheckFromList) return;

    if (clausesViewRef.current) {
      clausesViewRef.current.scrollTop = 0;
    }
    if (mccBesideRightStackRef.current) {
      mccBesideRightStackRef.current.scrollTop = 0;
    }
    setMccWorkflowScrollRequestToken((t) => t + 1);
  }, [solutionsSubView, viewMode]);

  React.useEffect(() => {
    if (solutionsSubView === 'list' || !isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
      setMccBesidePdfDocked(false);
    }
  }, [solutionsSubView]);
  React.useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const identity = await resolveDocumentIdentity();
        if (cancelled) return;
        setDocumentIdentity((prev) =>
          prev && documentIdentitiesMatch(prev, identity) ? prev : identity,
        );
      } catch (err) {
        console.error('[TaskPane] Failed to resolve document identity:', err);
        // Settle on an empty identity so Solutions falls back to the grid instead
        // of waiting forever on the document scan.
        if (!cancelled) {
          setDocumentIdentity((prev) => prev ?? { name: '', lastSaveTime: null, createdAt: null });
        }
      }
    };
    void refresh();
    window.addEventListener('focus', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const autoDetectAttemptedRef = React.useRef(false);
  /** After gating Ask/Agent/Editor, return here once Review party context is saved. */
  const pendingReturnTabRef = React.useRef<'ask' | 'agent' | 'editor' | null>(null);
  const previousTabBeforeProfileRef = React.useRef<NavTab>('review');
  /** Keep the active tab mounted (hidden) while Profile is open so state is preserved on close. */
  const [profileKeepAliveTab, setProfileKeepAliveTab] = React.useState<NavTab | null>(null);

  const isTabMounted = React.useCallback(
    (tab: NavTab) =>
      viewMode === tab ||
      profileKeepAliveTab === tab ||
      (tab === 'review' && reviewKeepAlive) ||
      (tab === 'clauses' && solutionsKeepAlive),
    [viewMode, profileKeepAliveTab, reviewKeepAlive, solutionsKeepAlive]
  );

  const isTabVisible = React.useCallback((tab: NavTab) => viewMode === tab, [viewMode]);

  React.useEffect(() => {
    if (viewMode === 'clauses') setSolutionsKeepAlive(true);
  }, [viewMode]);

  // Another document in this pane (Save As under a new name) starts Solutions over, as a new pane would.
  const solutionsDocumentRef = React.useRef('');
  React.useEffect(() => {
    const name = normalizeDocumentName(documentIdentity?.name ?? '');
    if (!name) return;
    const previous = solutionsDocumentRef.current;
    solutionsDocumentRef.current = name;
    if (previous && previous !== name) {
      solutionsScrollRef.current = { view: 0, stack: 0 };
      setSolutionsGeneration((n) => n + 1);
    }
  }, [documentIdentity]);

  // Solutions' scroll, as it was last on screen. A hidden view reads as unscrolled, so it is not recorded then.
  React.useEffect(() => {
    const view = clausesViewRef.current;
    if (!view) return undefined;
    const record = () => {
      if (view.clientHeight === 0) return;
      solutionsScrollRef.current = { view: view.scrollTop, stack: mccBesideRightStackRef.current?.scrollTop ?? 0 };
    };
    // Captured: the clause list scrolls in the MCC column, not in the view itself, while a beside panel is open.
    view.addEventListener('scroll', record, { capture: true, passive: true });
    return () => view.removeEventListener('scroll', record, { capture: true });
  }, [solutionsKeepAlive, solutionsGeneration]);

  // Shown again (another tab, or after signing in again): back to where it was.
  React.useLayoutEffect(() => {
    if (viewMode !== 'clauses' || !isAuthenticated) return undefined;
    const { view, stack } = solutionsScrollRef.current;
    if (!view && !stack) return undefined;
    const restore = () => {
      if (view && clausesViewRef.current) clausesViewRef.current.scrollTop = view;
      if (stack && mccBesideRightStackRef.current) mccBesideRightStackRef.current.scrollTop = stack;
    };
    restore();
    // Once more after the first frame: headers measured on showing can still change the height.
    const frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [viewMode, isAuthenticated]);

  const getSessionSnapshot = React.useCallback((): SessionNavigationSnapshot => {
    const effectiveViewMode: NavTab =
      viewMode === 'profile'
        ? profileKeepAliveTab ?? previousTabBeforeProfileRef.current
        : viewMode;
    return {
      viewMode: effectiveViewMode,
      solutionsSubView,
      selectedProjectId,
      selectedSubProjectId,
      reviewKeepAlive,
      documentName: normalizeDocumentName(documentIdentity?.name ?? ''),
    };
  }, [
    viewMode,
    profileKeepAliveTab,
    solutionsSubView,
    selectedProjectId,
    selectedSubProjectId,
    reviewKeepAlive,
    documentIdentity,
  ]);

  const [reviewRequiredModalTab, setReviewRequiredModalTab] = React.useState<
    'ask' | 'agent' | 'editor' | null
  >(null);

  const goToReviewForContext = React.useCallback((tab: 'ask' | 'agent' | 'editor') => {
    setReviewRequiredModalTab(tab);
  }, []);

  const confirmGoToReviewForContext = React.useCallback(() => {
    const tab = reviewRequiredModalTab ?? 'editor';
    pendingReturnTabRef.current = tab;
    setReviewKeepAlive(true);
    setReviewRequiredModalTab(null);
    setViewMode('review');
  }, [reviewRequiredModalTab]);

  const dismissReviewRequiredModal = React.useCallback(() => {
    setReviewRequiredModalTab(null);
  }, []);

  const handleReviewContextCommitted = React.useCallback(() => {
    setReviewKeepAlive(true);
    const next = pendingReturnTabRef.current ?? 'editor';
    pendingReturnTabRef.current = null;
    setViewMode(next);
  }, []);

  // Initialize services (following layer separation)
  const aiProvider = React.useMemo(() => createAIProvider('vertex'), []);
  const aiOrchestrator = React.useMemo(() => new AIOrchestrator(aiProvider), [aiProvider]);
  const rephrasingService = React.useMemo(
    () => new RephrasingService(aiOrchestrator),
    [aiOrchestrator]
  );
  const chatService = React.useMemo(
    () => new ChatService(rephrasingService),
    [rephrasingService]
  );

  // Capture real Word paragraph edits (OOXML + accepted-text deltas) for reverse-engineering edit mode.
  React.useEffect(() => {
    startWordParagraphEditCapture();
  }, []);

  React.useEffect(() => {
    apiService.setActiveProjectId(selectedProjectId || undefined);
  }, [selectedProjectId]);

  // Check authentication on mount and listen for token expiration
  React.useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(apiService.isAuthenticated());
      // Someone else signed in, here or in another Word window: this pane shows nothing of the last user's.
      const user = userOfToken(apiService.getCurrentAuthToken());
      if (user && paneUserRef.current && user !== paneUserRef.current) restartPane();
    };
    
    // Initial check
    checkAuth();
    
    // Listen for token expiration events
    const handleTokenExpired = () => {
      console.log('[TaskPane] Token expired event received, showing login screen');
      keepSession(getSessionSnapshot());
      setIsAuthenticated(false);
    };
    
    window.addEventListener('auth:token-expired', handleTokenExpired);
    
    // Check periodically in case token is set elsewhere
    const interval = setInterval(checkAuth, 1000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, [getSessionSnapshot, keepSession, restartPane]);

  React.useEffect(() => {
    // Hold off until the document is known: a snapshot saved without its document
    // name can never be trusted on the next open, forcing a needless re-scan.
    if (!isAuthenticated || !documentIdentity) return;
    keepSession(getSessionSnapshot());
  }, [isAuthenticated, documentIdentity, getSessionSnapshot]);

  // Poll for cross-check completion on MCC / Construction Loan sub-projects so the
  // results view refreshes once the backend marks step 3 as completed.
  React.useEffect(() => {
    if (!selectedSubProjectId || !isAuthenticated || !documentScopeSettled) {
      return;
    }

    let isActive = true;
    let pollInterval: NodeJS.Timeout | null = null;
    // Require N consecutive "completed" polls before refreshing, so we don't react
    // to a stale completed status from a previous run while a new cross-check is
    // still being kicked off in the background.
    let consecutiveCompletedCount = 0;
    /** First poll establishes baseline — if already completed, skip refresh (e.g. after re-login). */
    let baselineEstablished = false;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    // Stop instead of polling forever: a run that failed (or whose server
    // instance died — the progress endpoint reports that as a retryable error)
    // will never report "completed", and repeated failures mean nothing is
    // listening on the other end.
    const MAX_CONSECUTIVE_ERRORS = 5;
    let consecutiveErrors = 0;

    const pollCrossCheckProgress = async () => {
      try {
        // Progress only (~1 KB) — this used to download the whole sub-project
        // (multi-MB of results) every 2s just to read this one field.
        const response = await apiService.getCrossCheckProgress(selectedSubProjectId);

        if (!isActive) return;
        consecutiveErrors = 0;

        if (response.success && response.data) {
          const crossCheckProgress = response.data.cross_check_progress as
            | { workflow_step?: unknown; status?: unknown }
            | null;
          if (isCrossCheckWorkflowError(crossCheckProgress?.status)) {
            // The results view renders the failure; nothing left to wait for.
            stopPolling();
            return;
          }
          const isCompleted =
            crossCheckProgress?.workflow_step === 3 && crossCheckProgress?.status === 'completed';

          if (!baselineEstablished) {
            baselineEstablished = true;
            if (isCompleted) {
              // Cross-check was already done — no new completion to refresh for.
              stopPolling();
              return;
            }
          }

          if (isCompleted) {
            consecutiveCompletedCount += 1;
            if (consecutiveCompletedCount >= 3) {
              stopPolling();
              setRefreshClausesKey((prev) => prev + 1);
            }
          } else {
            consecutiveCompletedCount = 0;
          }
        }
      } catch (error) {
        console.error('[TaskPane] Error polling cross-check progress:', error);
        const status = (error as { response?: { status?: number } })?.response?.status;
        const authError = Boolean((error as { isAuthError?: boolean })?.isAuthError) || status === 401;
        consecutiveErrors += 1;
        if (authError || consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          stopPolling();
        }
      }
    };

    pollCrossCheckProgress();
    pollInterval = setInterval(pollCrossCheckProgress, 2000);

    return () => {
      isActive = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [selectedSubProjectId, isAuthenticated, documentScopeSettled]);

  React.useEffect(() => {
    // Until the open document owns the selection, `selectedSubProjectId` may still be
    // another document's — asking the backend about it would re-populate what the
    // document scan is about to clear.
    if (!selectedSubProjectId || !isAuthenticated || !documentScopeSettled) {
      setShowMccStartCrossCheckResume(false);
      setResumeParentProjectId(null);
      return;
    }
    if (!isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
      setShowMccStartCrossCheckResume(false);
      setResumeParentProjectId(null);
      return;
    }
    if (dismissedStartCrossCheckSubProjectIdRef.current === selectedSubProjectId) {
      setShowMccStartCrossCheckResume(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.getSubProjectDetails(selectedSubProjectId);
        if (cancelled) return;

        const awaiting = Boolean(
          response.success && response.data && isSubProjectAwaitingCrossCheckStart(response.data)
        );

        if (crossCheckStartInitiatedSubProjectIdRef.current === selectedSubProjectId) {
          const progress = response.data?.cross_check_progress as Record<string, unknown> | undefined;
          const status = String(progress?.status ?? '').toLowerCase();
          const workflowStarted =
            progress?.is_active === true ||
            status === 'running' ||
            status === 'in_progress' ||
            (typeof progress?.workflow_step === 'number' && progress.workflow_step >= 1);

          if (status === 'error' || status === 'failed') {
            crossCheckStartInitiatedSubProjectIdRef.current = null;
            setShowMccStartCrossCheckResume(awaiting);
          } else if (workflowStarted) {
            crossCheckStartInitiatedSubProjectIdRef.current = null;
            setShowMccStartCrossCheckResume(false);
          } else {
            // Auto-start HTTP may still be in flight — keep the results view.
            setShowMccStartCrossCheckResume(false);
          }
        } else {
          setShowMccStartCrossCheckResume(awaiting);
        }

        const parentId = response.data?.parent_project_id;
        if (typeof parentId === 'string' && parentId.trim()) {
          setResumeParentProjectId(parentId);
          setSelectedProjectId((current) => current || parentId);
        } else {
          setResumeParentProjectId(null);
        }
      } catch {
        if (!cancelled) {
          setShowMccStartCrossCheckResume(false);
          setResumeParentProjectId(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedSubProjectId, isAuthenticated, solutionsSubView, documentScopeSettled]);

  const handleSubProjectSelect = (subProjectId: string) => {
    if (subProjectId !== crossCheckStartInitiatedSubProjectIdRef.current) {
      crossCheckStartInitiatedSubProjectIdRef.current = null;
    }
    if (subProjectId !== dismissedStartCrossCheckSubProjectIdRef.current) {
      dismissedStartCrossCheckSubProjectIdRef.current = null;
    }
    setSelectedSubProjectId(subProjectId);
    if (subProjectId) {
      setShowMccCreateFlow(false); // Existing sub-project chosen — hide guided create wizard
      setViewMode('clauses');
    } else {
      setShowMccStartCrossCheckResume(false);
      setResumeParentProjectId(null);
    }
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    apiService.setActiveProjectId(projectId);
    setShowMccCreateFlow(false);
    setFilenameMatchOptions(null);
    if (projectId !== selectedProjectId) {
      setSelectedSubProjectId('');
    }
  };

  const handleLoginSuccess = () => {
    const snapshot = getSessionSnapshot();
    const t = apiService.getCurrentAuthToken();
    console.log('[Courtyard][AuthTrace] handleLoginSuccess', {
      tokenPresent: !!t,
      tokenFingerprint: t
        ? t.length <= 24
          ? `len=${t.length}`
          : `len=${t.length} head=${t.slice(0, 10)}…`
        : null,
      viewMode: snapshot.viewMode,
      selectedSubProjectId: snapshot.selectedSubProjectId || '(none)',
      selectedProjectId: snapshot.selectedProjectId || '(none)',
      solutionsSubView: snapshot.solutionsSubView,
    });
    // Start the idle / absolute-session clocks on successful login.
    markSessionStart();
    // Someone else than the one this pane was open for, or than the one whose place is kept on this
    // computer (a shared computer): the pane starts over for them.
    const user = userOfToken(t);
    if (user) {
      const previous = paneUserRef.current;
      paneUserRef.current = user;
      const cleared = claimUserState(user, { adoptUnowned: false });
      if (cleared || (previous !== null && previous !== user)) {
        restartPane();
        return;
      }
    }
    setIsAuthenticated(true);
  };

  /**
   * `restart`: the user signed out themselves, so nothing of what was open stays behind the sign-in
   * screen for whoever comes next. A sign-out for being idle keeps it for the user to come back to.
   */
  const handleSignOut = React.useCallback(async ({ restart = false }: { restart?: boolean } = {}) => {
    keepSession(getSessionSnapshot());
    try {
      await signOutFirebase();
    } catch (err) {
      console.warn('[TaskPane] Firebase sign out failed:', err);
    }
    apiService.clearAuthToken();
    setProfileKeepAliveTab(null);
    pendingReturnTabRef.current = null;
    setIsAuthenticated(false);
    if (restart) restartPane();
  }, [getSessionSnapshot, keepSession, restartPane]);

  // Idle timeout (60m) + absolute session cap (12h) for the Word pane.
  // Only real user input (pointerdown/keydown/touchstart) on the pane document
  // counts as activity — NOT background MCC polls and NOT Word document focus.
  // On timeout we run the normal sign-out (Firebase signOut + clearAuthToken +
  // login overlay). Refresh is separately gated in api.refreshAuthToken.
  React.useEffect(() => {
    if (!isAuthenticated) return;

    // Seed the clocks if the pane re-mounted / re-authenticated without going
    // through handleLoginSuccess (e.g. token restored from storage on reload).
    if (getSessionStart() == null) {
      markSessionStart();
    }

    // The same real-input events also drive the throttled activity ping that
    // keeps the scale-to-zero services warm.
    const uninstall = installUserActivityListeners(document, notifyUserActivityPing);

    const idleCheckInterval = setInterval(() => {
      if (shouldForceLogout()) {
        console.warn('[TaskPane] Session idle/expired — forcing sign-out');
        void handleSignOut();
      }
    }, 30_000);

    return () => {
      uninstall();
      resetActivityPing();
      clearInterval(idleCheckInterval);
    };
  }, [isAuthenticated, handleSignOut]);

  // Wake backend / engine / ClamAV as soon as the pane loads (before sign-in).
  React.useEffect(() => {
    sendPaneWarmup();
  }, []);

  // When switching between solutions, clear project/sub-project selection so the correct list is shown
  const prevSolutionRef = React.useRef<SolutionsSubView>(solutionsSubView);
  React.useEffect(() => {
    if (solutionsSubView === 'list') {
      setFilenameMatchLoading(false);
    }
    if (prevSolutionRef.current !== solutionsSubView && solutionsSubView !== 'list') {
      setSelectedProjectId('');
      setSelectedSubProjectId('');
      setShowMccCreateFlow(false);
      autoDetectAttemptedRef.current = false;
      if (isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
        setFilenameMatchLoading(true);
      } else {
        setFilenameMatchLoading(false);
      }
      prevSolutionRef.current = solutionsSubView;
    } else if (solutionsSubView !== 'list') {
      prevSolutionRef.current = solutionsSubView;
    }
  }, [solutionsSubView]);

  /**
   * Enter a solution on behalf of the document scan without tripping the
   * solution-switch reset above (which exists for *user* navigation between
   * solutions and would wipe the selection we are about to make).
   */
  const enterSolutionForDocument = React.useCallback(
    (solution: SolutionId, projectId: string, subProjectId: string, options: FilenameMatch[] | null) => {
      prevSolutionRef.current = solution;
      autoDetectAttemptedRef.current = true;
      setShowMccCreateFlow(false);
      setShowManualProjectSelect(false);
      setFilenameMatchLoading(false);
      setFilenameMatchOptions(options);
      setSolutionsSubView(solution);
      setSelectedProjectId(projectId);
      setSelectedSubProjectId(subProjectId);
    },
    [],
  );

  /** Open the client project a document-scan candidate points at. */
  const handleDocumentScanPick = React.useCallback(
    (match: FilenameMatch) => {
      const solution = resolveDocumentSolution([match]);
      if (solution.kind !== 'single') return;
      setDocumentScanOptions(null);
      enterSolutionForDocument(solution.solution, match.project_id, match.sub_project_id, null);
    },
    [enterSolutionForDocument],
  );

  /**
   * Resolve the open document to its solution before anything else is shown.
   *
   * Runs once per document. A restored selection is kept only when it belongs to
   * this document; otherwise it is dropped and the filename is looked up across
   * every solution, so "Solutions" opens where the document actually lives.
   */
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (!documentIdentity) return; // still reading the document
    const documentName = documentIdentity.name ?? '';
    const key = normalizeDocumentName(documentName);
    if (!key || key === 'document.docx') {
      setDocumentScopeSettled(true);
      return;
    }
    if (documentScannedForRef.current === key) return;
    documentScannedForRef.current = key;

    if (snapshotHasRestorableSelection(restoredSessionRef.current, documentName)) {
      log(
        `[TaskPane] Document scan skipped: restored selection already belongs to "${documentName}"`,
        {
          viewMode,
          solutionsSubView: restoredSessionRef.current.solutionsSubView,
          selectedProjectId: restoredSessionRef.current.selectedProjectId || '(none)',
          selectedSubProjectId: restoredSessionRef.current.selectedSubProjectId || '(none)',
        },
      );
      setDocumentScopeSettled(true);
      return;
    }
    log(`[TaskPane] Document scan starting for "${documentName}"`);

    // The restored selection belongs to a different document — start over.
    restoredSessionRef.current = clearDocumentScopedSelection(restoredSessionRef.current);
    prevSolutionRef.current = 'list';
    autoDetectAttemptedRef.current = false;
    setSolutionsSubView('list');
    setSelectedProjectId('');
    setSelectedSubProjectId('');
    setShowMccCreateFlow(false);
    setShowManualProjectSelect(false);
    setFilenameMatchOptions(null);
    setDocumentScanOptions(null);
    setFilenameMatchLoading(false);
    setDocumentScanLoading(true);

    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.getSubProjectByFilename(documentName);
        if (cancelled) return;
        const matches: FilenameMatch[] = response.success ? response.data?.matches ?? [] : [];
        documentMatchCacheRef.current = { documentName: key, matches };
        const resolution = resolveDocumentSolution(matches);
        log(
          `[TaskPane] Document scan: "${documentName}" -> ${resolution.kind} (${matches.length} raw matches)`,
          resolution.kind === 'none' ? '' : resolution,
        );
        if (resolution.kind !== 'none') {
          // Show what the scan resolved. Without this the work lands on the
          // Solutions tab while the restored tab stays in front, so the user
          // reopens the document and sees nothing happen.
          log(`[TaskPane] Document scan: showing Solutions tab (was "${viewMode}")`);
          setViewMode('clauses');
        }

        if (resolution.kind === 'single') {
          enterSolutionForDocument(
            resolution.solution,
            resolution.match.project_id,
            resolution.match.sub_project_id,
            null,
          );
        } else if (resolution.kind === 'choose' && resolution.solution) {
          // One solution, several client projects — ask inside that solution.
          enterSolutionForDocument(resolution.solution, '', '', resolution.matches);
        } else if (resolution.kind === 'choose') {
          // Candidates span solutions — ask before entering one.
          setDocumentScanOptions(resolution.matches);
        }
      } catch (err) {
        logError('[TaskPane] Document scan failed:', err);
      } finally {
        if (!cancelled) {
          setDocumentScanLoading(false);
          setDocumentScopeSettled(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, documentIdentity, enterSolutionForDocument]);

  // Auto-detect sub-project by matching the open Word document filename
  React.useEffect(() => {
    if (
      !isAuthenticated ||
      viewMode !== 'clauses' ||
      !isCrossCheckFilenameInferenceSolution(solutionsSubView) ||
      selectedSubProjectId
    ) {
      setFilenameMatchLoading(false);
      return;
    }
    if (autoDetectAttemptedRef.current) {
      return;
    }

    autoDetectAttemptedRef.current = true;
    setFilenameMatchLoading(true);
    let cancelled = false;

    const solution = solutionsSubView as SolutionId;

    (async () => {
      try {
        const filename = await DocumentExporter.getDocumentName();
        if (cancelled || !filename || filename === 'document.docx') {
          return;
        }

        // The document scan already asked the backend for this filename; reuse it
        // rather than paying for a second identical round trip.
        const cached = documentMatchCacheRef.current;
        let allMatches: FilenameMatch[];
        if (cached && cached.documentName === normalizeDocumentName(filename)) {
          allMatches = cached.matches;
        } else {
          console.log(`[TaskPane] Auto-detect: resolving filename "${filename}"`);
          const response = await apiService.getSubProjectByFilename(filename);
          if (cancelled) return;
          allMatches = response.success ? response.data?.matches ?? [] : [];
          documentMatchCacheRef.current = {
            documentName: normalizeDocumentName(filename),
            matches: allMatches,
          };
        }

        // Only candidates from the solution being viewed; a Construction Loan
        // client project must never surface under Master Cross Checks.
        const matches = matchesForSolution(allMatches, solution);

        if (matches.length === 1) {
          const match = matches[0];
          console.log(`[TaskPane] Auto-detect: unique match found – project=${match.project_name}, sub-project=${match.sub_project_name}`);
          setFilenameMatchOptions(null);
          setSelectedProjectId(match.project_id);
          setSelectedSubProjectId(match.sub_project_id);
        } else if (matches.length > 1) {
          console.log(`[TaskPane] Auto-detect: ${matches.length} matches found – showing disambiguation`);
          setFilenameMatchOptions(matches);
        } else {
          setFilenameMatchOptions(null);
          console.log('[TaskPane] Auto-detect: no matches found');
        }
      } catch (err) {
        console.error('[TaskPane] Auto-detect failed:', err);
      } finally {
        setFilenameMatchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, viewMode, solutionsSubView, selectedSubProjectId, autoDetectTrigger]);

  // Reset auto-detect flag and clear filename matches when user leaves MCC / Construction Loan
  React.useEffect(() => {
    if (!isCrossCheckFilenameInferenceSolution(solutionsSubView)) {
      autoDetectAttemptedRef.current = false;
      setFilenameMatchOptions(null);
      setShowManualProjectSelect(false);
      setFilenameMatchLoading(false);
    }
  }, [solutionsSubView]);

  return (
    <div className="task-pane" style={getUiScaleStyle(fontScale)}>
      {!isAuthenticated && (
        <div className="task-pane-login-overlay task-pane--login">
          <Login onLoginSuccess={handleLoginSuccess} />
        </div>
      )}
      <div
        className="task-pane-authenticated-shell"
        hidden={!isAuthenticated}
        aria-hidden={!isAuthenticated}
      >
      <div className="task-pane-content">
        {isTabMounted('review') && (
          <div
            className="task-pane-view-root"
            hidden={!isTabVisible('review')}
            aria-hidden={!isTabVisible('review')}
          >
            <ReviewView
              documentIdentity={documentIdentity}
              onReviewContextCommitted={handleReviewContextCommitted}
              onBusyChange={handleReviewBusyChange}
              chatService={chatService}
            />
          </div>
        )}

        {isTabMounted('editor') && (
          <div
            className="task-pane-view-root"
            hidden={!isTabVisible('editor')}
            aria-hidden={!isTabVisible('editor')}
          >
            <EditorView
              chatService={chatService}
              onRequireReview={() => goToReviewForContext('editor')}
            />
          </div>
        )}

        {isTabMounted('agent') && (
          <div
            className="task-pane-view-root"
            hidden={!isTabVisible('agent')}
            aria-hidden={!isTabVisible('agent')}
          >
            <AgentView
              parentProjectId={selectedProjectId || undefined}
              workflowHint={
                solutionsSubView === 'construction_loan'
                  ? 'construction_loan'
                  : solutionsSubView === 'master_cross_checks'
                  ? 'real_estate'
                  : undefined
              }
              onRequireReview={() => goToReviewForContext('agent')}
            />
          </div>
        )}

        {isTabMounted('ask') && (
          <div
            className="task-pane-view-root"
            hidden={!isTabVisible('ask')}
            aria-hidden={!isTabVisible('ask')}
          >
            <AskView onRequireReview={() => goToReviewForContext('ask')} />
          </div>
        )}

        {isTabMounted('clauses') && (
          <div
            key={solutionsGeneration}
            ref={clausesViewRef}
            className={`task-pane-view-root clauses-view${mccBesidePdfDocked ? ' clauses-view--mcc-beside-split' : ''}`}
            hidden={!isTabVisible('clauses')}
            aria-hidden={!isTabVisible('clauses')}
            style={
              mccBesidePdfDocked
                ? ({ ['--clauses-mcc-pdf-pct' as string]: `${mccBesidePdfPct}%` } as React.CSSProperties)
                : undefined
            }
          >
            {!documentScopeSettled || (solutionsSubView === 'list' && documentScanLoading) ? (
              <div
                className="clauses-placeholder clauses-filename-match-loading"
                dir={chromeDir}
                aria-busy="true"
                aria-live="polite"
              >
                <div className="filename-match-spinner" role="status" />
                <p>{t('solutions.projectPick.looking')}</p>
              </div>
            ) : solutionsSubView === 'list' && documentScanOptions && documentScanOptions.length > 0 ? (
              <FilenameMatchPicker
                matches={documentScanOptions}
                dir={chromeDir}
                onPick={handleDocumentScanPick}
                onChooseManually={() => setDocumentScanOptions(null)}
              />
            ) : solutionsSubView === 'list' ? (
              <SolutionsGrid
                onSelect={(id) => {
                  setSolutionsSubView(id);
                  if (isCrossCheckFilenameInferenceSolution(id)) {
                    setFilenameMatchLoading(true);
                  } else {
                    setFilenameMatchLoading(false);
                  }
                }}
              />
            ) : (
              <>
                {mccBesidePdfDocked && isCrossCheckFilenameInferenceSolution(solutionsSubView) && (
                  <>
                    <div ref={mccPdfBesideDockRef} className="clauses-view__mcc-pdf-dock" />
                    <div ref={mccBesideHandleSlotRef} className="clauses-view__mcc-beside-handle-slot" />
                  </>
                )}
                <div
                  ref={isCrossCheckFilenameInferenceSolution(solutionsSubView) ? mccBesideRightStackRef : undefined}
                  className={
                    isCrossCheckFilenameInferenceSolution(solutionsSubView) ? 'clauses-view__mcc-cc-shell' : undefined
                  }
                >
                {(!isCrossCheckFilenameInferenceSolution(solutionsSubView) ||
                  showManualProjectSelect ||
                  !!selectedProjectId) && (
                  <ProjectSelector
                    solutionId={solutionsSubView as SolutionId}
                    onSubProjectSelect={handleSubProjectSelect}
                    onProjectSelect={handleProjectSelect}
                    onCreateNewSubProject={
                      (solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') &&
                      selectedProjectId
                        ? () => {
                            setSelectedSubProjectId('');
                            setShowMccCreateFlow(true);
                          }
                        : undefined
                    }
                    guidedSetupActive={showMccCreateFlow}
                    selectedSubProjectId={selectedSubProjectId}
                    selectedProjectId={selectedProjectId}
                    refreshTrigger={subProjectRefreshTrigger}
                    isAuthenticated={isAuthenticated}
                  />
                )}
                {selectedSubProjectId ? (
                  (solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') ? (
                    showMccStartCrossCheckResume && (selectedProjectId || resumeParentProjectId) ? (
                      <div className="clauses-guided-initiation">
                        <MasterCrossChecksInitiation
                          projectId={selectedProjectId || resumeParentProjectId || ''}
                          resumeSubProjectId={selectedSubProjectId}
                          workflowVariant={
                            solutionsSubView === 'construction_loan' ? 'construction_loan' : 'master_cross_checks'
                          }
                          onComplete={(subProjectId) => {
                            crossCheckStartInitiatedSubProjectIdRef.current = subProjectId;
                            dismissedStartCrossCheckSubProjectIdRef.current = null;
                            setShowMccStartCrossCheckResume(false);
                            setResumeParentProjectId(null);
                            setSelectedSubProjectId(subProjectId);
                            setSubProjectRefreshTrigger((t) => t + 1);
                            setRefreshClausesKey((k) => k + 1);
                          }}
                          onLater={(subProjectId) => {
                            dismissedStartCrossCheckSubProjectIdRef.current = subProjectId;
                            setShowMccStartCrossCheckResume(false);
                            setResumeParentProjectId(null);
                            setSelectedSubProjectId(subProjectId);
                          }}
                          onCancel={() => {
                            setShowMccStartCrossCheckResume(false);
                            setResumeParentProjectId(null);
                          }}
                          onError={(errMsg) => {
                            crossCheckStartInitiatedSubProjectIdRef.current = null;
                            setShowMccStartCrossCheckResume(false);
                            setResumeParentProjectId(null);
                            setSelectedSubProjectId('');
                            setCrossCheckStartError(errMsg);
                          }}
                        />
                      </div>
                    ) : (
                      <CrossCheckResultsView
                        key={refreshClausesKey}
                        subProjectId={selectedSubProjectId}
                        projectId={selectedProjectId}
                        isAuthenticated={isAuthenticated}
                        pdfBesideDockRef={mccPdfBesideDockRef}
                        besideHandleSlotRef={mccBesideHandleSlotRef}
                        besideResizeOuterRef={clausesViewRef}
                        besidePdfChromeInDock={mccBesidePdfDocked}
                        onMccBesidePdfLayoutChange={handleMccBesidePdfLayoutChange}
                        workflowScrollRequestToken={mccWorkflowScrollRequestToken}
                        besideRightScrollRef={mccBesideRightStackRef}
                        crossCheckStartDeclined={
                          dismissedStartCrossCheckSubProjectIdRef.current === selectedSubProjectId
                        }
                        active={isTabVisible('clauses')}
                      />
                    )
                  ) : null
                ) : selectedProjectId ? (
                  <>
                    {!selectedSubProjectId && (
                      <>
                        {(solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') &&
                          !showMccCreateFlow && (
                          <div className="clauses-placeholder" dir={chromeDir}>
                            <p>
                              {t('solutions.projectPick.guidedLead')}
                            </p>
                            <button
                              type="button"
                              className="clauses-manual-toggle clauses-guided-create"
                              onClick={() => setShowMccCreateFlow(true)}
                            >
                              {t('solutions.projectPick.newClientProject')}
                            </button>
                            {isCrossCheckFilenameInferenceSolution(solutionsSubView) && (
                              <button
                                type="button"
                                className="clauses-manual-toggle"
                                onClick={() => {
                                  documentMatchCacheRef.current = null;
                                  autoDetectAttemptedRef.current = false;
                                  setFilenameMatchLoading(true);
                                  setAutoDetectTrigger((t) => t + 1);
                                  setShowManualProjectSelect(false);
                                  setSelectedProjectId('');
                                  setSelectedSubProjectId('');
                                }}
                              >
                                {t('solutions.projectPick.showSuggested')}
                              </button>
                            )}
                          </div>
                        )}
                        {crossCheckStartError && !showMccCreateFlow && (
                          <div className="error-message" style={{ margin: '8px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <span>{crossCheckStartError}</span>
                            <button
                              onClick={() => setCrossCheckStartError(null)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 8px', fontSize: '14px', lineHeight: 1 }}
                              aria-label={t('solutions.projectPick.dismissError')}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        {(solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') &&
                          showMccCreateFlow && (
                          <div className="clauses-guided-initiation">
                            <MasterCrossChecksInitiation
                              projectId={selectedProjectId}
                              workflowVariant={
                                solutionsSubView === 'construction_loan' ? 'construction_loan' : 'master_cross_checks'
                              }
                              onComplete={(subProjectId) => {
                                crossCheckStartInitiatedSubProjectIdRef.current = subProjectId;
                                setShowMccCreateFlow(false);
                                setShowMccStartCrossCheckResume(false);
                                setSelectedSubProjectId(subProjectId);
                                setSubProjectRefreshTrigger((t) => t + 1);
                                setRefreshClausesKey((k) => k + 1);
                              }}
                              onCancel={() => setShowMccCreateFlow(false)}
                              onError={(errMsg) => {
                                crossCheckStartInitiatedSubProjectIdRef.current = null;
                                // The user was already navigated to the results view (by onComplete),
                                // so we navigate back to the sub-project list and show the error.
                                setSelectedSubProjectId('');
                                setShowMccCreateFlow(false);
                                setCrossCheckStartError(errMsg);
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {(solutionsSubView === 'master_cross_checks' || solutionsSubView === 'construction_loan') && (
                      <div className="clauses-load-divider" aria-hidden />
                    )}
                    {showManualProjectSelect ? (
                        <div className="clauses-placeholder" dir={chromeDir}>
                          <p>{t('solutions.projectPick.selectAbove')}</p>
                          <button
                            type="button"
                            className="clauses-manual-toggle"
                            onClick={() => {
                              documentMatchCacheRef.current = null;
                              autoDetectAttemptedRef.current = false;
                              setFilenameMatchLoading(true);
                              setAutoDetectTrigger((t) => t + 1);
                              setShowManualProjectSelect(false);
                            }}
                          >
                            {t('solutions.projectPick.showSuggested')}
                          </button>
                        </div>
                      ) : filenameMatchLoading ? (
                        <div
                          className="clauses-placeholder clauses-filename-match-loading"
                          dir={chromeDir}
                          aria-busy="true"
                          aria-live="polite"
                        >
                          <div className="filename-match-spinner" role="status" />
                          <p>{t('solutions.projectPick.looking')}</p>
                        </div>
                      ) : filenameMatchOptions && filenameMatchOptions.length > 0 ? (
                        <FilenameMatchPicker
                          matches={filenameMatchOptions}
                          dir={chromeDir}
                          onPick={(m) => {
                            setFilenameMatchOptions(null);
                            setSelectedProjectId(m.project_id);
                            setSelectedSubProjectId(m.sub_project_id);
                          }}
                          onChooseManually={() => {
                            setFilenameMatchLoading(false);
                            setShowManualProjectSelect(true);
                          }}
                        />
                      ) : (
                        <div className="clauses-placeholder" dir={chromeDir}>
                          <p>{t('solutions.projectPick.noMatch')}</p>
                          <button
                            type="button"
                            className="clauses-manual-toggle"
                            onClick={() => {
                              setFilenameMatchLoading(false);
                              setShowManualProjectSelect(true);
                            }}
                          >
                            {t('solutions.projectPick.chooseManually')}
                          </button>
                        </div>
                      )}
                  </>
                )}
                </div>
              </>
            )}
          </div>
        )}
        
        {isTabMounted('proofread') && (
          <div
            className="task-pane-view-root"
            hidden={!isTabVisible('proofread')}
            aria-hidden={!isTabVisible('proofread')}
          >
            <ProofreadView />
          </div>
        )}
        {viewMode === 'profile' && (
          <ProfileView
            onClose={() => {
              setViewMode(previousTabBeforeProfileRef.current);
              setProfileKeepAliveTab(null);
            }}
            onSignOut={() => {
              void handleSignOut({ restart: true });
            }}
            onPreferencesChange={handlePreferencesChange}
          />
        )}
      </div>

      <ReviewRequiredModal
        isOpen={reviewRequiredModalTab != null}
        onCancel={dismissReviewRequiredModal}
        onConfirm={confirmGoToReviewForContext}
      />
      <BottomNav
        activeTab={viewMode}
        reviewBusy={reviewBusy}
        onTabChange={(tab) => {
          if (tab === 'profile') {
            if (viewMode !== 'profile') {
              previousTabBeforeProfileRef.current = viewMode;
              setProfileKeepAliveTab(viewMode);
            }
          } else {
            setProfileKeepAliveTab(null);
            if (tab !== 'review' && !hasReviewPartyContextComplete()) {
              setReviewKeepAlive(true);
            }
          }
          setViewMode(tab);
          // Coming back to Solutions finds it where it was left; a click on it while on it goes back to the grid.
          if (tab === 'clauses' && viewMode === 'clauses' && solutionsSubView !== 'list') {
            setSolutionsSubView('list');
          }
        }}
      />
      </div>
    </div>
  );
};

export default TaskPane;

